import { GetStaticProps, NextPage } from 'next'
import DefaultErrorPage from 'next/error'
import { Dispatch, SetStateAction, useState } from 'react'
import { FaCrown } from 'react-icons/fa'
import { RankingArticleList } from 'web_core/assets/article'
import { LoadingSpinner } from 'web_core/assets/spinners'
import { ESArticle } from 'web_core/elastic_search'
import { useESArticles } from 'web_core/hooks'
import { Serialized, deserializeArray } from 'web_core/utils'
import { DefaultLayout } from '~/components/Layout/Default'
import { ContentNavSelector } from '~/components/Utils/ContentNavSelector'
import { Heading } from '~/components/Utils/Heading'
import { RankingMonthSelector } from '~/components/Utils/RankingMonthSelector'
import { SearchFilter } from '~/components/Utils/SearchFilter'
import { useLayoutProperty } from '~/context/layout_property'
import { LayoutStaticProps } from '~/static_props/layout'
import {
  MonthlyRankingParams,
  getArticlesRankingStaticProps,
  getMonthlyRankingStaticPaths,
} from '~/static_props/ranking'
import { monthlyArticleRankingBreadcrumb } from '~/utils/breadcrumb'
import { isValidRankingPeriod } from '~/utils/ranking'

export const getStaticPaths = getMonthlyRankingStaticPaths

type Props = {
  articles: Serialized<ESArticle>[]
  year: string | null
  month: string | null
} & LayoutStaticProps

export const getStaticProps: GetStaticProps<
  Props,
  MonthlyRankingParams
> = async ({ locale = 'ja', params }) => {
  if (!params) throw new Error('The params is undefined.')
  return getArticlesRankingStaticProps(locale, params.year, params.month)
}

type ContentsMenuProps = {
  categoryId: string | undefined
  setCategoryId: Dispatch<SetStateAction<string | undefined>>
  year: string
  month: string
}

const ContentsMenu = ({
  categoryId,
  setCategoryId,
  year,
  month,
}: ContentsMenuProps) => {
  const { headerHeight } = useLayoutProperty()
  return (
    <div
      className="position-sticky bg-white pt-2 pb-2 mb-2 mb-md-4"
      style={{ top: headerHeight }}
    >
      <ContentNavSelector
        active="articles"
        link="/ranking/[contents]"
        className="mb-2 mb-md-4"
      />
      <div className="d-flex justify-content-between gap-4">
        <RankingMonthSelector
          type={'articles'}
          className="py-1"
          selectedYear={year}
          selectedMonth={month}
        />
        <SearchFilter
          className="fs-7 fs-md-6"
          categoryFilter={{ onSelectId: setCategoryId, id: categoryId }}
        />
      </div>
    </div>
  )
}

const Page: NextPage<Props> = ({
  articles: staticArticles,
  year,
  month,
  categories,
  popularProfiles,
}) => {
  const [categoryId, setCategoryId] = useState<string>()
  const { articles } = useESArticles(
    year !== null && month !== null
      ? {
          type: 'ranking',
          locale: 'ja',
          year: Number(year),
          month: Number(month),
          category_id: categoryId,
        }
      : null,
    deserializeArray(staticArticles, {})
  )
  if (year === null || month === null || !isValidRankingPeriod(year, month))
    return <DefaultErrorPage statusCode={404} />

  const breadcrumb = monthlyArticleRankingBreadcrumb(year, month)

  return (
    <DefaultLayout
      head={{
        title: `${year}年${month}月の記事ランキング`,
        description: `${year}年${month}月にユーザからの高評価が多かった上位20記事をご覧いただけます。`,
        keyword: 'ランキング',
      }}
      navLinks={{ pc: true, tablet: true, sp: true, active: 'ranking' }}
      categoriesMenu={{ pc: true, sp: true }}
      staticCategories={categories}
      staticPopularProfiles={popularProfiles}
      leftSidebar
      rightSideBar
    >
      {breadcrumb.toReactNode()}
      <Heading title="ランキング" icon={FaCrown} className="mb-0" />
      <ContentsMenu
        categoryId={categoryId}
        setCategoryId={setCategoryId}
        year={year}
        month={month}
      />
      {articles ? (
        <RankingArticleList
          articles={articles}
          className="grid g-cols-1 g-xl-cols-2"
        />
      ) : (
        <LoadingSpinner />
      )}
    </DefaultLayout>
  )
}

export default Page
